import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import { usePermissionTemplateListApi } from '@/common/api/modules/permissionTemplate'
import {
  CreateSiteOperatorResponse,
  CreateSiteOperatorVars,
  UpdateSiteOperatorResponse,
  UpdateSiteOperatorVars
} from '@/common/api/params/siteOperator'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { siteOperatorEditPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowSavedToast } from '@/common/app/toast'
import { registerNumberOption } from '@/common/form/register'
import { refreshCodeUnique } from '@/common/form/schema'
import { SiteOperator } from '@/common/model/siteOperator'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Loading from '@/components/view/atoms/icon/Loading'
import { FieldType, TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  DEFAULT_SITE_OPERATOR_SCHEMA,
  SiteOperatorFormSchemaType,
  convertSiteOperatorSchemaToVars,
  convertSiteOperatorToSchema,
  siteOperatorNameLabelMapping,
  siteOperatorSchema
} from '@/components/container/form/modules/site/operator/siteOperatorSchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormSelector from '@/components/container/form/widgets/FormSelector'
import FormTextField, { FormTextFieldType } from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  operator?: SiteOperator
  onRefetch?(): void
}

const SiteOperatorForm = ({ operator, onRefetch }: Props) => {
  const navigate = useNavigate()
  const siteId = useSiteId()

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<SiteOperatorFormSchemaType>({
    resolver: zodResolver(siteOperatorSchema),
    values: operator
      ? convertSiteOperatorToSchema(operator)
      : {
          ...DEFAULT_SITE_OPERATOR_SCHEMA,
          siteId: siteId
        },
    mode: 'onChange'
  })

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, siteOperatorNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!operator })

  const { data: templates, isLoading: isTemplateLoading } = usePermissionTemplateListApi({
    site_id: siteId
  })

  const { trigger } = useCallMutation<
    CreateSiteOperatorResponse | UpdateSiteOperatorResponse,
    CreateSiteOperatorVars | UpdateSiteOperatorVars
  >(
    operator ? ApiPath.UPDATE_SITE_OPERATOR : ApiPath.CREATE_SITE_OPERATOR,
    {
      onSuccess: (response) => {
        showSaved(response.name)
        operator && onRefetch ? onRefetch() : navigate(siteOperatorEditPath(siteId, response.id))
        reset(operator ? convertSiteOperatorToSchema(operator) : DEFAULT_SITE_OPERATOR_SCHEMA)
        refreshCodeUnique()
      }
    },
    operator ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<SiteOperatorFormSchemaType> = (data) => {
    const vars = convertSiteOperatorSchemaToVars(siteId, data)
    return trigger(vars)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={operator ? operator.name : 'オペレーターを新規作成する'}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {operator ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={operator ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={operator ? '未反映の変更があります' : '登録されていない新しいオペレーター'}
          onCancel={() =>
            reset(operator ? convertSiteOperatorToSchema(operator) : DEFAULT_SITE_OPERATOR_SCHEMA)
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本情報</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='email'>
                メールアドレス
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('email')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.email?.message}
                  size={TextBoxSize.LARGE}
                  placeholder='operator@example.com'
                />
                <FormError message={errors.email?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required={!operator} id='password'>
                パスワード
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('password')}
                  fieldType={!operator ? FieldType.REQUIRED : undefined}
                  error={!!errors.password?.message}
                  size={TextBoxSize.LARGE}
                  type={FormTextFieldType.PASSWORD}
                  placeholder={operator ? '変更しない場合は空のままにしてください' : ''}
                />
                <FormError message={errors.password?.message} />
              </FormRow>
            </FormRow>
            <FormRow>
              <FormLabel required id='name'>
                名前
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('name')}
                  fieldType={FieldType.REQUIRED}
                  error={!!errors.name?.message}
                  placeholder='山田 太郎'
                />
                <FormError message={errors.name?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>権限設定</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='permissionTemplateId'>権限テンプレート</FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                {isTemplateLoading ? (
                  <Loading />
                ) : (
                  <FormSelector
                    register={register('permissionTemplateId', registerNumberOption)}
                    choices={templates?.map((t) => ({ name: t.name, value: t.id })) ?? []}
                    error={!!errors.permissionTemplateId?.message}
                    noSelectName='権限テンプレートを選択してください'
                    noSelectable
                  />
                )}
                <FormError message={errors.permissionTemplateId?.message} />
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {operator ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default SiteOperatorForm
